import axiosInstance from "core/api/axiosInstance";

/**
 * Obtiene todos los grupos VIP Came
 */
export const getGroupsService = async (signal) => {
  try {
    const { data } = await axiosInstance.get("/api/tp/get-iserra-groups", {
      signal,
      timeout: 20000,
    });

    if (!data || typeof data !== "object" || !Array.isArray(data.data) || data.success === false) {
      console.error("❌ Respuesta inválida de grupos:", data);
      return {
        success: false,
        error: "Respuesta inválida del servidor",
        data: [],
        total: 0,
      };
    }

    const groupsArray = Array.isArray(data.data) ? data.data : [];

    const sortedGroups = groupsArray.sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: "base" })
    );

    return {
      success: true,
      data: sortedGroups,
      total: sortedGroups.length,
      message: data.msm || "Grupos obtenidos exitosamente",
    };
  } catch (error) {
    console.error("❌ Error al obtener grupos VIP Came:", error);

    let errorMessage = "Error al obtener grupos VIP Came";

    if (error.response) {
      errorMessage =
        error.response.data?.message ||
        `Error ${error.response.status}: ${error.response.statusText}`;
    } else if (error.request) {
      errorMessage = "No se pudo conectar con el servidor";
    } else {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: [],
      total: 0,
    };
  }
};

/**
 * Función auxiliar para formatear fecha (sin ajuste de zona horaria)
 */
const formatDate = (dateString) => {
  if (!dateString) return "N/A";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";

  return date.toLocaleString("es-ES", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

/**
 * Función para comparar fechas (string a timestamp)
 */
const parseDateToTimestamp = (dateString) => {
  if (!dateString || dateString === "N/A" || dateString === "---") return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date.getTime();
};

/**
 * Función CORREGIDA para determinar si un vehículo está dentro y en qué tipo de parqueadero
 *
 * Lógica:
 * 1. Los usuarios primero entran a General, luego pueden pasar a VIP
 * 2. VIP: tiene ent_vip válida Y (sal_vip es N/A O sal_vip < ent_vip)
 * 3. General: tiene ent_gen válida Y (sal_gen es N/A O sal_gen < ent_gen) Y NO está en VIP
 * 4. Fuera: tiene salida válida (salida > entrada) en ambos casos
 */
const determineParkingStatus = (parkingInfo) => {
  if (!parkingInfo) return { isInside: false, parkingType: null };

  // Parsear fechas
  const entGenTimestamp = parseDateToTimestamp(parkingInfo.ent_gen);
  const salGenTimestamp = parseDateToTimestamp(parkingInfo.sal_gen);
  const entVipTimestamp = parseDateToTimestamp(parkingInfo.ent_vip);
  const salVipTimestamp = parseDateToTimestamp(parkingInfo.sal_vip);

  // Verificar VIP: tiene entrada VIP y (no tiene salida VIP O salida VIP es menor que entrada VIP)
  const hasValidVipEntry = entVipTimestamp !== null && !isNaN(entVipTimestamp);
  const hasValidVipExit =
    salVipTimestamp !== null && !isNaN(salVipTimestamp) && salVipTimestamp > entVipTimestamp;
  const isInsideVip = hasValidVipEntry && !hasValidVipExit;

  // Verificar General: tiene entrada General y (no tiene salida General O salida General es menor que entrada General)
  const hasValidGenEntry = entGenTimestamp !== null && !isNaN(entGenTimestamp);
  const hasValidGenExit =
    salGenTimestamp !== null && !isNaN(salGenTimestamp) && salGenTimestamp > entGenTimestamp;
  const isInsideGeneral = hasValidGenEntry && !hasValidGenExit;

  // Prioridad: VIP primero (porque los usuarios pueden estar en VIP después de entrar a General)
  if (isInsideVip) {
    return { isInside: true, parkingType: "vip" };
  }

  if (isInsideGeneral) {
    return { isInside: true, parkingType: "general" };
  }

  // Si no está en VIP ni en General, está fuera
  return { isInside: false, parkingType: null };
};

/**
 * Obtiene todos los vehículos que están actualmente DENTRO del parqueadero con sus horas
 * VERSIÓN CORREGIDA - Filtra salidas menores que entradas
 */
export const getCurrentParkingVehiclesService = async (signal) => {
  try {
    const { data } = await axiosInstance.get("/api/tp/get-iserra-vehicles/all", {
      signal,
      timeout: 20000,
    });

    if (
      !data ||
      typeof data !== "object" ||
      !Array.isArray(data.data) ||
      !Array.isArray(data.time) ||
      data.success === false
    ) {
      console.error("❌ Respuesta inválida de vehículos en parqueadero:", data);
      return {
        success: false,
        error: "Respuesta inválida del servidor",
        data: [],
        total: 0,
      };
    }

    const vehiclesArray = Array.isArray(data.data) ? data.data : [];
    const timeArray = Array.isArray(data.time) ? data.time : [];

    // Crear un mapa de time por placa
    const timeMap = {};
    timeArray.forEach((timeItem) => {
      if (timeItem.plate) {
        timeMap[timeItem.plate] = timeItem;
      }
    });

    const formattedVehicles = vehiclesArray.map((vehicle) => {
      const timeInfo = timeMap[vehicle.plate];

      // Si no hay timeInfo, el vehículo no tiene movimientos registrados
      if (!timeInfo) {
        return {
          idvehicle: vehicle.idvehicle?.toString() || "N/A",
          plate: vehicle.plate || "Sin placa",
          tid: vehicle.tid || "N/A",
          enterat: formatDate(vehicle.enterat),
          enteratRaw: vehicle.enterat,
          isInside: false,
          ent_gen: null,
          sal_gen: null,
          ent_vip: null,
          sal_vip: null,
          parkingType: null,
        };
      }

      // Usar los datos del timeInfo directamente
      const entGenRaw = timeInfo.ent_gen || null;
      const salGenRaw = timeInfo.sal_gen || null;
      const entVipRaw = timeInfo.ent_vip || null;
      const salVipRaw = timeInfo.sal_vip || null;

      // Determinar estado de parqueadero
      const parkingInfoForCheck = {
        ent_gen: entGenRaw,
        sal_gen: salGenRaw,
        ent_vip: entVipRaw,
        sal_vip: salVipRaw,
      };

      const { isInside, parkingType } = determineParkingStatus(parkingInfoForCheck);

      return {
        idvehicle: vehicle.idvehicle?.toString() || "N/A",
        plate: vehicle.plate || "Sin placa",
        tid: vehicle.tid || "N/A",
        enterat: formatDate(vehicle.enterat),
        enteratRaw: vehicle.enterat,
        isInside: isInside,
        // Solo mostrar salidas si son mayores que las entradas
        ent_gen: entGenRaw,
        sal_gen:
          salGenRaw && parseDateToTimestamp(salGenRaw) > parseDateToTimestamp(entGenRaw)
            ? salGenRaw
            : null,
        ent_vip: entVipRaw,
        sal_vip:
          salVipRaw && parseDateToTimestamp(salVipRaw) > parseDateToTimestamp(entVipRaw)
            ? salVipRaw
            : null,
        parkingType: parkingType,
        // Para debugging
        _raw: {
          ent_gen: entGenRaw,
          sal_gen: salGenRaw,
          ent_vip: entVipRaw,
          sal_vip: salVipRaw,
        },
      };
    });

    // Filtrar SOLO los vehículos que realmente están dentro
    const vehiclesInside = formattedVehicles.filter((v) => v.isInside === true);

    // Crear un mapa por placa para acceso rápido
    const parkingMap = {};
    vehiclesInside.forEach((vehicle) => {
      parkingMap[vehicle.plate] = vehicle;
    });

    return {
      success: true,
      data: vehiclesInside,
      parkingMap: parkingMap,
      total: vehiclesInside.length,
    };
  } catch (error) {
    console.error("❌ Error al obtener vehículos en parqueadero:", error);
    return {
      success: false,
      error: error.message,
      data: [],
      parkingMap: {},
      total: 0,
    };
  }
};

/**
 * Obtiene TODOS los vehículos de un grupo (registrados + información de tiempo si están dentro)
 */
export const getAllGroupVehiclesService = async (idgroup, signal) => {
  if (!idgroup) return { success: false, error: "ID de grupo requerido", data: [] };
  return getVipSnapshotService(idgroup, signal);
};

/**
 * Obtiene todos los vehículos con sus grupos
 */
export const getAllVehiclesWithGroupsService = async (signal) => {
  try {
    const { data } = await axiosInstance.get("/api/tp/get-iserra", { signal, timeout: 20000 });

    if (!data || typeof data !== "object" || !Array.isArray(data.data) || data.success === false) {
      console.error("❌ Respuesta inválida de vehículos con grupos:", data);
      return {
        success: false,
        error: "Respuesta inválida del servidor",
        data: [],
        total: 0,
        cuposInfo: null,
      };
    }

    const vehiclesArray = Array.isArray(data.data) ? data.data : [];

    const vehiclesWithGroups = vehiclesArray.map((vehicle) => ({
      idvehicle: vehicle.idvehicle?.toString() || "N/A",
      idgroup: vehicle.idgroup,
      groupName: vehicle.name || "Sin grupo",
      plate: vehicle.plate || "Sin placa",
    }));

    const vehiclesMap = {};
    vehiclesWithGroups.forEach((vehicle) => {
      vehiclesMap[vehicle.idvehicle] = vehicle;
    });

    let cuposInfo = null;
    if (data.dataQuote && data.dataQuote.dataQuote && data.dataQuote.dataQuote.length > 0) {
      const quoteData = data.dataQuote.dataQuote[0];
      cuposInfo = {
        cupo_total: quoteData.cupo_total || 0,
        cupos_usados: parseInt(quoteData.cupos_usados) || 0,
        cupos_disponibles: parseInt(quoteData.cupos_disponibles) || 0,
      };
    }

    return {
      success: true,
      data: vehiclesWithGroups,
      vehiclesMap: vehiclesMap,
      total: vehiclesWithGroups.length,
      message: data.msm || "Vehículos obtenidos exitosamente",
      cuposInfo: cuposInfo,
    };
  } catch (error) {
    console.error("❌ Error al obtener vehículos con grupos:", error);
    return {
      success: false,
      error: error.message || "Error al obtener vehículos con grupos",
      data: [],
      vehiclesMap: {},
      total: 0,
      cuposInfo: null,
    };
  }
};

/**
 * Obtiene vehículos que están actualmente dentro del parqueadero de un grupo específico
 */
export const getParkingVehiclesService = async (idgroup) => {
  try {
    if (!idgroup) {
      console.warn("⚠️ ID de grupo no proporcionado a getParkingVehiclesService");
      return {
        success: false,
        error: "ID de grupo requerido",
        data: [],
        total: 0,
      };
    }

    const { data } = await axiosInstance.get(`/api/tp/get-iserra-vehicles/${idgroup}`);

    if (!data || typeof data !== "object" || !Array.isArray(data.data) || data.success === false) {
      console.error("❌ Respuesta inválida de vehículos en parqueadero:", data);
      return {
        success: false,
        error: "Respuesta inválida del servidor",
        data: [],
        total: 0,
      };
    }

    const vehiclesArray = Array.isArray(data.data) ? data.data : [];

    const formattedVehicles = vehiclesArray.map((vehicle) => ({
      idvehicle: vehicle.idvehicle?.toString() || "N/A",
      plate: vehicle.plate || "Sin placa",
      countrycode: vehicle.countrycode || "COL",
      vehicleType:
        vehicle.idvehicletype === 1
          ? "Automóvil"
          : vehicle.idvehicletype === 2
          ? "Motocicleta"
          : vehicle.idvehicletype === 3
          ? "Bicicleta"
          : vehicle.idvehicletype === 11
          ? "Otro"
          : `Tipo ${vehicle.idvehicletype || "Desconocido"}`,
      tid: vehicle.tid || "N/A",
      enterat: formatDate(vehicle.enterat),
      enteratRaw: vehicle.enterat,
      outat:
        vehicle.outat === "2000-01-01T05:00:00.000Z"
          ? "Dentro"
          : vehicle.outat
          ? formatDate(vehicle.outat)
          : "N/A",
      cyclestatus:
        vehicle.cyclestatus === 1
          ? "Activo"
          : vehicle.cyclestatus === 2
          ? "Completado"
          : vehicle.cyclestatus === 3
          ? "Anulado"
          : `Estado ${vehicle.cyclestatus || "Desconocido"}`,
      status:
        vehicle.status === 1
          ? "Activo"
          : vehicle.status === 0
          ? "Inactivo"
          : `Estado ${vehicle.status || "Desconocido"}`,
      createdat: formatDate(vehicle.createdat),
      updatedat: formatDate(vehicle.updatedat),
    }));

    return {
      success: true,
      data: formattedVehicles,
      total: formattedVehicles.length,
      message: data.msm || "Vehículos en parqueadero obtenidos exitosamente",
    };
  } catch (error) {
    console.error(`❌ Error al obtener vehículos del grupo ${idgroup}:`, error);

    let errorMessage = `Error al obtener vehículos del grupo ${idgroup}`;

    if (error.response) {
      if (error.response.status === 404) {
        errorMessage = "Grupo no encontrado o sin vehículos en parqueadero";
      } else {
        errorMessage =
          error.response.data?.message ||
          `Error ${error.response.status}: ${error.response.statusText}`;
      }
    } else if (error.request) {
      errorMessage = "No se pudo conectar con el servidor";
    } else {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: [],
      total: 0,
    };
  }
};

// Mantener las funciones existentes por compatibilidad
export const getAllVehiclesService = async () => {
  return await getAllVehiclesWithGroupsService();
};

export const getGroupVehiclesService = getAllGroupVehiclesService;

// A failed occupancy request is unknown, never proof that registered cars are outside.
export async function getVipSnapshotService(groupId, signal) {
  const [registered, parking] = await Promise.all([
    getAllVehiclesWithGroupsService(signal),
    getCurrentParkingVehiclesService(signal),
  ]);
  if (!registered.success)
    return {
      success: false,
      error: registered.error || "No se pudo consultar el listado de vehículos",
      data: [],
    };
  const occupancyKnown = parking.success;
  const parkingMap = parking.parkingMap || {};
  const allData = registered.data.map((vehicle) => {
    const current = parkingMap[vehicle.plate];
    return {
      ...vehicle,
      isInside: occupancyKnown ? Boolean(current) : null,
      parkingType: occupancyKnown ? current?.parkingType || null : null,
      ent_gen: current?.ent_gen || null,
      sal_gen: current?.sal_gen || null,
      ent_vip: current?.ent_vip || null,
      sal_vip: current?.sal_vip || null,
      enterat: current?.enterat || null,
      tid: current?.tid || null,
    };
  });
  const data =
    groupId == null
      ? allData
      : allData.filter((vehicle) => String(vehicle.idgroup) === String(groupId));
  return {
    success: true,
    data,
    allData,
    total: data.length,
    parkingCount: occupancyKnown
      ? data.filter((vehicle) => vehicle.isInside === true).length
      : null,
    parkingMap: occupancyKnown ? parkingMap : {},
    occupancyKnown,
    occupancyError: occupancyKnown
      ? ""
      : "No se pudo confirmar la ocupación del parqueadero. Los vehículos se muestran Sin información; no es posible calcular los cupos disponibles. Pulsa Actualizar para reintentar.",
    cuposInfo: registered.cuposInfo,
  };
}
